<template>
  <div class="map-container">
    <div :id="mapId" class="map-content"></div>
  </div>
</template>

<script>
import 'ol/ol.css';
import Map from 'ol/Map';
import View from 'ol/View';
import XYZ from 'ol/source/XYZ';
import * as olProj from 'ol/proj';
import { getUUID } from '@/utils/util';
import TileLayer from 'ol/layer/Tile';
import storage from 'store';
import { LonLat } from '@/store/mutation-types';
import VectorSource from 'ol/source/Vector';
import Feature from 'ol/Feature';
import Point from 'ol/geom/Point';
import { Cluster } from 'ol/source';
import VectorLayer from 'ol/layer/Vector';
import { Style, Icon, Text, Fill, Stroke } from 'ol/style';
import Overlay from 'ol/Overlay';
import { unByKey } from 'ol/Observable';
import GeoJSON from 'ol/format/GeoJSON';
import axios from 'axios'
import { ACCESS_TOKEN } from '@/store/mutation-types'


export default {
  name: 'BigUserMap',
  props: {
    showLayerData: {
      type: Object,
      required: true
    },
    layerQueryData: {
      type: Object,
      required: true
    }
  },
  watch: {
    showLayerData: {
      handler (newVal, oldVal) {
        // console.log(Object.keys(newVal).length === 0);
        console.log('对象发生变化:', newVal)
        this.toggleTypeVisibility(newVal.type, newVal.show);
        // 这里可以执行你的业务逻辑
      },
    },
    layerQueryData: {
      handler (newVal, oldVal) {
        console.log('对象发生变化:', newVal)
        this.moveThePosition(newVal)
      },
    }
  },
  data () {
    return {
      bigUserUrl: 'smart-water-major-users/major/users/user/user/all/list/tenant/gzip',
      extLayerUrl: '/pipe-network/zone-metering/water-supply-range',
      areaData: [],
      mapId: getUUID(), // 地图id
      map: null, // 地图实例
      baseLayer: null, // 底图图层
      popup: null, // 弹窗元素
      popupOverlay: null, // 弹窗覆盖层
      hiddenTypes: [], // 存储要隐藏的类型
      clusterLayers: [], // 聚合图层数组
      areaLayer: null, // 片区图层
      mapClickListener: null, // 地图点击事件监听器
      viewResolutionListener: null, // 视图分辨率变化监听器
    };
  },
  mounted () {
    this.initMap(); // 初始化地图
    // 确保地图初始化完成后再添加点
    this.$nextTick(() => {
      this.addRandomPointsAroundCenter();
      this.getAreaData();
    });
  },
  beforeDestroy () {
    console.log('开始清理 BigUserMap 组件资源...');

    // 1. 移除事件监听器
    if (this.mapClickListener) {
      unByKey(this.mapClickListener);
      this.mapClickListener = null;
    }
    if (this.viewResolutionListener) {
      unByKey(this.viewResolutionListener);
      this.viewResolutionListener = null;
    }

    // 2. 清理弹窗覆盖层
    if (this.popupOverlay && this.map) {
      this.map.removeOverlay(this.popupOverlay);
      this.popupOverlay = null;
    }
    if (this.popup) {
      this.popup.remove();
      this.popup = null;
    }

    // 3. 清理所有聚合图层
    if (this.clusterLayers && this.clusterLayers.length > 0 && this.map) {
      this.clusterLayers.forEach(layer => {
        const source = layer.getSource();
        if (source) {
          // 清理聚合源
          const clusterSource = source.getSource();
          if (clusterSource) {
            if (clusterSource.clear) clusterSource.clear();
            if (clusterSource.dispose) clusterSource.dispose();
          }
          if (source.clear) source.clear();
          if (source.dispose) source.dispose();
        }
        this.map.removeLayer(layer);
        if (layer.dispose) layer.dispose();
      });
      this.clusterLayers = [];
    }

    // 4. 清理片区图层
    if (this.areaLayer && this.map) {
      const source = this.areaLayer.getSource();
      if (source) {
        if (source.clear) source.clear();
        if (source.dispose) source.dispose();
      }
      this.map.removeLayer(this.areaLayer);
      if (this.areaLayer.dispose) this.areaLayer.dispose();
      this.areaLayer = null;
    }

    // 5. 清理底图图层
    if (this.baseLayer && this.map) {
      const source = this.baseLayer.getSource();
      if (source) {
        if (source.clear) source.clear();
        if (source.dispose) source.dispose();
      }
      this.map.removeLayer(this.baseLayer);
      if (this.baseLayer.dispose) this.baseLayer.dispose();
      this.baseLayer = null;
    }

    // 6. 清理所有图层
    if (this.map) {
      const layers = this.map.getLayers().getArray().slice();
      layers.forEach(layer => {
        const source = layer.getSource();
        if (source) {
          if (source.clear) source.clear();
          if (source.dispose) source.dispose();
        }
        this.map.removeLayer(layer);
        if (layer.dispose) layer.dispose();
      });

      // 7. 移除所有交互
      const interactions = this.map.getInteractions().getArray().slice();
      interactions.forEach(interaction => {
        this.map.removeInteraction(interaction);
      });

      // 8. 移除所有叠加层
      const overlays = this.map.getOverlays().getArray().slice();
      overlays.forEach(overlay => {
        this.map.removeOverlay(overlay);
      });

      // 9. 销毁地图实例
      this.map.setTarget(null);
      this.map.dispose();
      this.map = null;
    }

    console.log('BigUserMap 组件资源清理完成');
  },
  methods: {
    // 初始化地图
    initMap () {
      this.setupGcjProjection();
      const center = this.getInitialCenter();

      this.map = new Map({
        controls: [],
        view: new View({
          center: center,
          projection: 'EPSG:4326',
          zoom: 12,
          maxZoom: 28,
          minZoom: 1,
        }),
        target: this.mapId,
        layers: []
      });

      this.createBaseLayer();
      this.createAreaLayer();
    },
    // 坐标系转换
    setupGcjProjection () {
      // 检查是否已经注册过GCJ-02投影
      let gcjMecator = olProj.get('GCJ-02');

      if (!gcjMecator) {
        // 如果没有注册过，则创建并注册
        const gcj02Extent = [-20037508.342789244, -20037508.342789244, 20037508.342789244, 20037508.342789244];
        gcjMecator = new olProj.Projection({ code: 'GCJ-02', extent: gcj02Extent, units: 'm' });
        olProj.addProjection(gcjMecator);
        olProj.addCoordinateTransforms('EPSG:4326', gcjMecator, window.projzh.ll2gmerc, window.projzh.gmerc2ll);
        olProj.addCoordinateTransforms('EPSG:3857', gcjMecator, window.projzh.smerc2gmerc, window.projzh.gmerc2smerc);
        console.log('GCJ-02投影已注册');
      } else {
        console.log('GCJ-02投影已存在，直接使用');
      }

      this.gcjMercator = gcjMecator;
    },
    // 中心定位
    getInitialCenter () {
      if (this.geo) {
        try {
          return JSON.parse(this.geo).coordinates;
        } catch (e) {
          console.error('解析geo属性失败:', e);
        }
      }
      return storage.get(LonLat);
    },
    // 加载底图（必应地图）
    createBaseLayer () {
      const projection = 'GCJ-02';
      const maxZoom = 18;

      console.log('创建底图，使用投影:', projection, 'maxZoom:', maxZoom);

      // 获取GCJ-02投影
      const gcjMecator = olProj.get('GCJ-02');

      // 构建 XYZ 数据源配置
      const xyzConfig = {
        projection: gcjMecator,
        crossOrigin: 'anonymous',
        cacheSize: 2048, // 大幅增加缓存大小，从256提升到2048
        maxZoom: maxZoom,
        wrapX: true, // 启用水平环绕
        transition: 0, // 禁用瓦片加载过渡动画，加快渲染
        interpolate: false, // 禁用图像平滑处理，加快渲染
        tileUrlFunction: (coord) => {
          return this.getVETileUrl(coord[0], coord[1], coord[2]);
        },
        tileSize: 256,
        tilePixelRatio: 1, // 使用标准像素比，不使用高清瓦片
        reprojectionErrorThreshold: 0.5 // 重投影误差阈值，降低精度要求以提升速度
      };

      this.baseLayer = new TileLayer({
        properties: {
          name: '底图',
          title: '必应地图',
          isBaseLayer: true
        },
        preload: 1, // 预加载1级瓦片
        source: new XYZ(xyzConfig),
        useInterimTilesOnError: false, // 瓦片加载错误时不使用临时瓦片
        renderMode: 'image', // 使用image模式渲染，比vector快
        className: 'dark-base-map' // 反色处理
      });

      this.map.getLayers().insertAt(0, this.baseLayer);
      console.log('底图创建完成，最大缩放级别:', maxZoom);
    },

    // 必应地图瓦片URL生成器
    getVETileUrl (z, x, y) {
      let quadKey = '';
      let tileX = x;
      let tileY = y;

      for (let i = 0; i < z; i++) {
        quadKey = ((tileX & 1) + 2 * (tileY & 1)).toString() + quadKey;
        tileX >>= 1;
        tileY >>= 1;
      }

      return 'http://dynamic.t0.tiles.ditu.live.com/comp/ch/' + quadKey + '?it=G,VE,BX,L,LA&mkt=zh-cn,syr&n=z&og=111&ur=CN';
    },
    // 获取片区数据
    getAreaData () {
      return this.$http.get(this.extLayerUrl + '/WATER_SUPPLY_RANGE').then(res => {
        // 确保areaData是数组格式
        this.areaData = Array.isArray(res.data) ? res.data : [res.data];
        console.log('片区数据:', this.areaData);
        // 数据获取后，添加面到地图
        this.addAreaToMap();
        console.log('片区数据加载完成');
      }).catch(error => {
        console.error('片区数据加载失败:', error);
        throw error;
      });
    },
    // 创建片区面图层
    createAreaLayer () {
      console.log('创建片区面图层...');

      // 创建片区数据源
      this.areaSource = new VectorSource({
        useSpatialIndex: true,
        wrapX: false
      });

      // 创建片区面图层
      this.areaLayer = new VectorLayer({
        source: this.areaSource,
        style: new Style({
          fill: new Fill({
            color: 'rgba(77, 246, 230, 0.15)' // 蓝色半透明填充
          }),
          stroke: new Stroke({
            color: '#4df6e6', // 蓝色边框
            width: 2
          })
        }),
        renderMode: 'image',
        updateWhileAnimating: true,
        updateWhileInteracting: true
      });

      // 将片区图层插入到底图之后
      this.map.getLayers().insertAt(1, this.areaLayer);
      console.log('片区面图层创建完成');
    },
    // 添加片区面到地图
    addAreaToMap () {
      if (!this.areaSource || !this.areaData || this.areaData.length === 0 || !this.areaData[0].geoJson) {
        console.log('片区数据源或数据不存在');
        return;
      }

      try {
        console.log('开始添加片区面数据...');

        // 解析GeoJSON数据
        const geoJsonData = JSON.parse(this.areaData[0].geoJson);
        console.log('解析的片区GeoJSON数据:', geoJsonData);

        // 创建GeoJSON格式器
        const geoJsonFormat = new GeoJSON({
          dataProjection: 'EPSG:4326', // 源数据投影
          featureProjection: 'EPSG:4326' // 目标投影
        });

        // 读取要素
        const features = geoJsonFormat.readFeatures(geoJsonData);
        console.log('读取的片区要素数量:', features.length);

        // 为要素添加属性
        features.forEach(feature => {
          feature.setProperties({
            layerName: this.areaData.layerName,
            layerCode: this.areaData.layerCode,
            id: this.areaData.id,
            type: '片区'
          });
        });

        // 批量添加要素，提升性能
        this.areaSource.addFeatures(features);
        console.log('成功添加片区面要素到地图');

        // 预加载面数据到缓存
        this.areaSource.forEachFeature(feature => {
          // 触发要素的几何体计算，预加载到缓存
          feature.getGeometry();
        });

      } catch (error) {
        console.error('添加片区面数据失败:', error);
      }
    },
    async getBigUserUrlData () {
      const token = storage.get(ACCESS_TOKEN)
      const params = {
        responseType: 'arraybuffer',
        headers: {
          Authorization: token
        }
      }
      const url = `${process.env.VUE_APP_API_BASE_URL}${this.bigUserUrl}`
      const res = await axios.get(url, params);

      const uint8Array = new Uint8Array(res.data);

      // 将 Uint8Array 解码为字符串
      const textDecoder = new TextDecoder('utf-8');
      const jsonString = textDecoder.decode(uint8Array);

      console.log('解码后的字符串长度:', jsonString.length);
      // 解析 JSON
      const jsonData = JSON.parse(jsonString);

      return jsonData;
    },
    // 生成点
    async addRandomPointsAroundCenter () {
      const features = [];
      try {
        const res = await this.getBigUserUrlData();
        console.log(res);

        // 使用工具类解压
        // const decompressedData = await GzipUtil.parseGzipData(res);
        // console.log('解压后的数据:', decompressedData);

        res.forEach(item => {
          // 检查坐标是否为空
          if (!item.convertLongitude || !item.convertLatitude) {
            return;
          }
          let type = '1';
          if (item.normalAbnormal == 1 && item.waterMeterType == 1) {
            type = '1';
          } else if (item.normalAbnormal == 1 && item.waterMeterType == 2) {
            type = '2';
          } else if (item.normalAbnormal == 2 && item.waterMeterType == 1) {
            type = '3';
          } else if (item.normalAbnormal == 2 && item.waterMeterType == 2) {
            type = '4';
          }
          item.type = type;


          // 确保坐标是数字类型
          const longitude = parseFloat(item.convertLongitude);
          const latitude = parseFloat(item.convertLatitude);

          // 检查坐标是否有效
          if (isNaN(longitude) || isNaN(latitude)) {
            return;
          }

          features.push(
            new Feature({
              geometry: new Point([longitude, latitude]),
              attributes: item
            })
          );
        });
        console.log(features);

        // 等待循环完成后再继续执行
        if (features.length === 0) {
          console.error('No features generated');
          return;
        }

        const vectorSource = new VectorSource();
        vectorSource.addFeatures(features);

        // 按类型分组要素
        const featuresByType = {};
        features.forEach(feature => {
          const type = feature.get('attributes')?.type;
          if (!featuresByType[type]) {
            featuresByType[type] = [];
          }
          featuresByType[type].push(feature);
        });

        // 为每种类型创建单独的聚合图层
        this.clusterLayers = [];
        Object.keys(featuresByType).forEach(type => {
          // 创建该类型的向量源
          const typeVectorSource = new VectorSource();
          typeVectorSource.addFeatures(featuresByType[type]);

          // 创建该类型的聚合源
          const typeClusterSource = new Cluster({
            distance: 40, // 聚合距离，像素单位
            source: typeVectorSource
          });

          // 获取该类型的图标路径
          let iconPath;
          switch (type) {
            case '1': iconPath = require('@/assets/bigusers/largeScreen/waterMeterIcon1.svg'); break;
            case '2': iconPath = require('@/assets/bigusers/largeScreen/waterMeterIcon2.svg'); break;
            case '3': iconPath = require('@/assets/bigusers/largeScreen/waterMeterIcon3.svg'); break;
            case '4': iconPath = require('@/assets/bigusers/largeScreen/waterMeterIcon4.svg'); break;
          }

          // 创建该类型的聚合图层
          const typeClusterLayer = new VectorLayer({
            source: typeClusterSource,
            style: (feature) => {
              // 检查该类型是否被隐藏
              if (this.hiddenTypes.includes(type)) {
                return null; // 不渲染隐藏的类型
              }

              // 创建样式数组
              const styles = [
                new Style({
                  image: new Icon({
                    src: iconPath, // 使用对应类型的 SVG
                    scale: 0.6, // 适当调整大小
                  })
                })
              ];

              // 如果是聚合点（包含多个要素），添加数量标签
              const clusterFeatures = feature.get('features');
              if (clusterFeatures.length > 1) {
                styles.push(
                  new Style({
                    text: new Text({
                      text: clusterFeatures.length.toString(),
                      font: 'bold 12px Arial',
                      fill: new Fill({ color: '#fff' }),
                      stroke: new Stroke({ color: '#000', width: 2 }),
                      offsetY: -15, // 向上偏移，显示在图标上方
                      textAlign: 'center',
                      textBaseline: 'middle'
                    })
                  })
                );
              }

              return styles;
            }
          });

          this.clusterLayers.push(typeClusterLayer);
        });

        // 将多个聚合图层添加到地图
        this.clusterLayers.forEach(layer => {
          // 给图层设置名称以便识别
          layer.set('name', `clusterLayer_${Object.keys(featuresByType)[this.clusterLayers.indexOf(layer)]}`);
          this.map.addLayer(layer);
        });

        console.log('图层已添加到地图，features数量:', features.length);
        console.log('地图图层数量:', this.map.getLayers().getLength());

        // 初始化弹窗
        this.initPopup();

        // 添加点击事件并保存引用
        this.mapClickListener = this.map.on('click', (evt) => {
          this.handleMapClick(evt);
        });

        // 添加缩放监听事件并保存引用
        this.viewResolutionListener = this.map.getView().on('change:resolution', () => {
          if (this.popupOverlay && this.popupOverlay.getPosition() != undefined) {
            this.popupOverlay.setPosition(undefined);
          }
        });

      } catch (error) {
        console.error('Error loading big user data:', error);
      }
    },
    // 初始化弹窗
    initPopup () {
      // 创建弹窗元素
      this.popup = document.createElement('div');
      this.popup.className = 'ol-popup';

      // 创建关闭按钮
      // const closer = document.createElement('a');
      // closer.className = 'ol-popup-closer';
      // closer.href = '#';
      // this.popup.appendChild(closer);

      // 创建内容容器
      const content = document.createElement('div');
      content.className = 'popup-content';
      this.popup.appendChild(content);

      // 创建覆盖层
      this.popupOverlay = new Overlay({
        element: this.popup,
        autoPan: true,
        autoPanAnimation: {
          duration: 250
        },
        offset: [-112, -252] // 向上偏移20像素
      });

      this.map.addOverlay(this.popupOverlay);

      // 点击关闭按钮时隐藏弹窗
      // closer.onclick = (e) => {
      //   e.preventDefault();
      //   this.popupOverlay.setPosition(undefined);
      // };
    },
    // 处理地图点击事件
    handleMapClick (evt) {
      // 获取点击位置
      const coordinate = evt.coordinate;

      // 获取点击处的要素
      const features = this.map.getFeaturesAtPixel(evt.pixel, {
        layerFilter: (layer) => {
          return layer.get('name') && layer.get('name').startsWith('clusterLayer_');
        },
        hitTolerance: 10 // 点击容差
      });

      if (!features || features.length === 0) {
        this.popupOverlay.setPosition(undefined);
        return;
      }

      // 获取集群中的第一个要素
      const cluster = features[0];
      const clusteredFeatures = cluster.get('features');

      // 显示第一个点的信息
      this.showSinglePopup(clusteredFeatures[0], coordinate);
    },
    // 显示单点弹窗 
    // feature 为聚合点  data 为搜索数据 coordinate 为坐标
    showSinglePopup (feature, coordinate, data) {
      const content = this.popup.querySelector('.popup-content');
      let attributes = null;
      if (feature == null) {
        attributes = data;
      } else {
        attributes = feature.get('attributes');
      }

      let html = ``;
      html += `<div class="popup-body">`;
      html += `<p><strong>用户名称:</strong> <span title="${attributes.userName || ''}">${attributes.userName || ''}</span></p>`;
      html += `<p><strong>用户编码:</strong> <span title="${attributes.userCode || ''}">${attributes.userCode || ''}</span></p>`;
      html += `<p><strong>手机号码:</strong> <span title="${attributes.phone || ''}">${attributes.phone || ''}</span></p>`;
      html += `<p><strong>用水类型:</strong> <span title="${attributes.waterMeterTypeName || ''}">${attributes.waterMeterTypeName || ''}</span></p>`;
      html += `<p><strong>上月用水量:</strong> <span title="${attributes.waterConsumption || ''}">${attributes.waterConsumption || ''}</span></p>`;
      html += `<p><strong>水表编号:</strong> <span title="${attributes.waterMeterNumber || ''}">${attributes.waterMeterNumber || ''}</span></p>`;
      html += `<p><strong>用户地址:</strong> <span title="${attributes.userAddress || ''}">${attributes.userAddress || ''}</span></p>`;
      html += `<p><strong>所属片区:</strong> <span title="${attributes.belongingArea || ''}">${attributes.belongingArea || ''}</span></p>`;
      html += `</div>`;

      content.innerHTML = html;
      this.popupOverlay.setPosition(coordinate);
    },
    // 切换类型的显示/隐藏
    toggleTypeVisibility (type, show) {
      if (show) {
        // 如果要显示，从 hiddenTypes 移除
        this.hiddenTypes = this.hiddenTypes.filter(t => t != type);
      } else {
        // 如果要隐藏，添加到 hiddenTypes
        if (!this.hiddenTypes.includes(type)) {
          this.hiddenTypes.push(type.toString());
        }
      }
      //清除弹框显示
      if (this.popupOverlay.getPosition() != undefined) {
        this.popupOverlay.setPosition(undefined);
      }
      // 强制刷新所有聚合图层
      const clusterLayers = this.map.getLayers().array_
        .filter(l => l.get('name') && l.get('name').startsWith('clusterLayer_'));
      clusterLayers.forEach(layer => {
        layer.getSource().refresh();
      });
    },
    //移动点位
    moveThePosition (data) {

      this.map.getView().setZoom(15);
      const longitude = parseFloat(data.convertLongitude);
      const latitude = parseFloat(data.convertLatitude);
      console.log(longitude, latitude);
      this.showSinglePopup(null, [longitude, latitude], data);
      // 检查坐标是否有效
      if (isNaN(longitude) || isNaN(latitude)) {
        this.$message.warning('此点位没有坐标，无法定位');
        return;
      }
      // 设置中心点并缩放到18级别
      this.map.getView().setCenter([longitude, latitude]);


    }
  }
};
</script>

<style lang="less" scoped>
.map-container {
  width: 100%;
  height: 100%;
  position: relative;
  background: #101114;

  .map-content {
    width: 100%;
    height: 100%;
  }
}

::v-deep .dark-base-map canvas {
  filter: grayscale(98%) invert(100%) sepia(20%) hue-rotate(180deg) saturate(1600%) brightness(90%) contrast(90%);
}

::v-deep .ol-popup {
  width: 235px;
  height: 239px;
  background: url(~@/assets/bigusers/largeScreen/popup.png) no-repeat;
  background-size: 100% 100%;
  user-select: none;
  /* 阻止文本选择 */
  -webkit-user-select: none;
  /* Safari */
  -moz-user-select: none;
  /* Firefox */
  -ms-user-select: none;
  /* IE10+/Edge */

  /* 确保元素不会被当作可点击输入 */
  pointer-events: auto;
  /* 允许点击事件，但不产生光标 */
  padding-top: 20px;
}

::v-deep .popup-body {
  padding-left: 30px;
  font-size: 12px;

  p {
    display: flex;
    align-items: center;
    /* 垂直居中 */
    margin: 0.5em 0;
    /* 可选：调整段落间距 */
  }
}

::v-deep .popup-body strong {
  min-width: 65px;
  /* 固定标签宽度，让冒号对齐 */
  text-align: right;
  /* 右对齐标签 */
  margin-right: 8px;
  /* 标签和内容之间的间距 */
  color: #fff;
}

::v-deep .popup-body span {
  color: #8BECF7;
  display: inline-block;
  max-width: 100px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>